/**
 * 物件マスタストア (Pinia)。HubSpot の物件カスタムオブジェクトの写し。
 *
 * この app が避難マップを作る物件は「既設または設置確定」= 必ず HubSpot にある。
 * よって種別は推定ではなく**正解表**として引ける (実測: 名前推定62% → マスタ94%)。
 *
 * 他ストアと違い**同梱シードを持たない**。src/data/ に置くとバンドルされ、
 * ログイン前でも11,000件超の顧客物件リストが取得できてしまうため。
 * 投入は scripts/import-hubspot-buildings.mjs の出力を取込UIから importEntries()。
 */
import { defineStore } from 'pinia';
import { computed, ref, shallowRef, watch, type Ref } from 'vue';
import type { BuildingMasterEntry } from '@/types/building';
import { createPersistedCrud, type PersistedCrud } from '@/lib/stores/persisted-crud';
import { createFirestoreCrud, type FirestoreCrud } from '@/lib/stores/firestore-crud';
import { isFirebaseConfigured } from '@/lib/firebase/init';
import { useAuthStore } from '@/stores/authStore';
import type { SaveState } from '@/lib/firebase/save-pipeline';
import { normalizeFacilityName } from '@/lib/knowledge/normalize';

const STORAGE_KEY = 'evacuation-map.building-master.v1';

let storeInstantiated = false;

function watchBinding<S, D extends S>(src: Ref<D>, dst: Ref<S>): () => void {
  return watch(src, (v) => { dst.value = v; }, { immediate: true });
}

/** ストア未生成なら何もしない、生成済みなら flush する (ログアウト時用)。 */
export async function flushBuildingStoreIfActive(): Promise<void> {
  if (!storeInstantiated) return;
  await useBuildingStore().flushAll();
}

export const useBuildingStore = defineStore('building', () => {
  storeInstantiated = true;

  const firestoreMode = isFirebaseConfigured();
  const crud = shallowRef<
    PersistedCrud<BuildingMasterEntry> | FirestoreCrud<BuildingMasterEntry> | undefined
  >(undefined);
  const entries = computed(() => crud.value?.items.value ?? []);
  const ready = ref(false);
  const saveState = ref<SaveState | 'local'>('local');

  /**
   * 正規化名 → エントリ。同名の別ビルが71名義あるため配列で持つ。
   * 名前一本での確定は避け、確定は常に id (hubspotPropertyId) で行う。
   */
  const byName = computed(() => {
    const m = new Map<string, BuildingMasterEntry[]>();
    for (const e of entries.value) {
      const k = normalizeFacilityName(e.name);
      if (!k) continue;
      const list = m.get(k);
      if (list) list.push(e);
      else m.set(k, [e]);
    }
    return m;
  });

  function getEntry(id: string): BuildingMasterEntry | undefined {
    return crud.value?.get(id);
  }

  /** 正規化名で引く。候補が2件以上なら曖昧なので null (呼び出し側で選ばせる)。 */
  function findByName(name: string): BuildingMasterEntry | null {
    const hits = byName.value.get(normalizeFacilityName(name));
    return hits?.length === 1 ? hits[0]! : null;
  }

  /** 名前・住所の部分一致検索 (マスタ選択コンボボックス用)。 */
  function search(query: string, limit = 30): BuildingMasterEntry[] {
    const q = normalizeFacilityName(query);
    if (!q) return [];
    const out: BuildingMasterEntry[] = [];
    for (const e of entries.value) {
      if (normalizeFacilityName(e.name).includes(q)) {
        out.push(e);
        if (out.length >= limit) break;
      }
    }
    return out;
  }

  /**
   * 取り込み。同一 id は上書き、無いものは追加 (削除はしない)。
   * HubSpot 側の名寄せ結果を反映するときは、消えた id を別途 remove すること。
   */
  function importEntries(list: BuildingMasterEntry[]): { added: number; updated: number } {
    let added = 0;
    let updated = 0;
    for (const e of list) {
      if (!e.id || !e.name) continue;
      if (crud.value?.get(e.id)) {
        crud.value.update(e.id, { ...e });
        updated += 1;
      } else {
        crud.value?.insert(e);
        added += 1;
      }
    }
    return { added, updated };
  }

  function flushAll(): Promise<void> {
    const c = crud.value;
    return c && 'flushAll' in c ? c.flushAll() : Promise.resolve();
  }

  if (firestoreMode) {
    const auth = useAuthStore();
    let boundEmail: string | null = null;
    let stopBindings: Array<() => void> = [];
    let disposeCurrent: (() => void) | null = null;

    const initFirestore = (email: string, displayName: string | null): void => {
      for (const stop of stopBindings) stop();
      stopBindings = [];
      const fsCrud = createFirestoreCrud<BuildingMasterEntry>({
        kind: 'building_master',
        email,
        displayName,
        deferLoad: true,
      });
      crud.value = fsCrud;
      stopBindings.push(watchBinding(fsCrud.ready, ready));
      stopBindings.push(watchBinding(fsCrud.saveState, saveState));
      disposeCurrent = fsCrud.dispose;
      boundEmail = email;
      // シード注入はしない (同梱しない方針)。空のまま取込を待つ。
      fsCrud.startLoad();
    };

    watch(
      () => auth.status,
      (status) => {
        if (status === 'ready' && auth.email && auth.email !== boundEmail) {
          disposeCurrent?.();
          initFirestore(auth.email, auth.displayName);
        } else if (status !== 'ready') {
          boundEmail = null;
          ready.value = false;
        }
      },
      { immediate: true },
    );
  } else {
    crud.value = createPersistedCrud<BuildingMasterEntry>({
      storageKey: STORAGE_KEY,
      label: '物件マスタ',
      initial: () => [],
    });
    ready.value = true;
  }

  return {
    entries,
    ready,
    saveState,
    flushAll,
    getEntry,
    findByName,
    search,
    importEntries,
  };
});
